import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "@digital-twins/design-system/icons";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { ArchiveCreativeEntityProps } from "@/types/creative-library";
export function ArchiveCreativeEntity({
  kind,
  token,
  disabled,
  onArchived,
}: ArchiveCreativeEntityProps) {
  const { t } = useTranslation("app");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  async function archive() {
    setSaving(true);
    setError(false);
    try {
      await creativeLibraryApi.archive(kind, token);
      onArchived();
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          className="mr-auto text-destructive"
          disabled={disabled}
          data-testid={`creative-archive-${kind}`}
        >
          {t("creativeLibrary.archive")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("creativeLibrary.archiveTitle")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("creativeLibrary.archiveHint")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && (
          <Text size="sm" className="text-destructive" role="alert">
            {t("creativeLibrary.saveFailed")}
          </Text>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>
            {t("creativeLibrary.cancel")}
          </AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={saving}
            onClick={() => void archive()}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("creativeLibrary.archive")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
